import React, { useMemo, useState } from 'react';
import { Link, matchPath, Outlet, useLocation } from 'react-router-dom';
import { Button, Drawer, Grid, Layout, Menu, MenuProps } from 'antd';
import { LogoutOutlined, MenuOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import { useAuth } from '../auth/AuthContext';
import { signOutApp } from '../auth/signOutApp';

const { Header, Content, Footer } = Layout;

export const APP_NAV_ROUTES = [
    { path: '/', label: 'Home' },
    { path: '/test-catalog', label: 'Test catalog' },
    { path: '/test-tree', label: 'Test tree' },
    { path: '/project-tests', label: 'Project tests' },
    { path: '/firebase-storage', label: 'Firebase storage' },
    { path: '/postgres-storage', label: 'Postgres storage' },
    { path: '/ebook', label: 'Ebook' },
    { path: '/account', label: 'Account' },
] as const;

function resolveNavSelection(
    pathname: string,
    routes: readonly { path: string; label: string }[]
): string {
    const exact = routes.find((r) => r.path === pathname);
    if (exact) return exact.path;
    const prefix = [...routes]
        .sort((a, b) => b.path.length - a.path.length)
        .find((r) => r.path !== '/' && !!matchPath({ path: `${r.path}/*`, end: false }, pathname));
    return prefix?.path ?? '/';
}

/**
 * Authenticated shell: main nav + outlet + sign out.
 */
export function MainLayout() {
    const screens = Grid.useBreakpoint();
    const isMobileNav = !screens.md;
    const location = useLocation();
    const [navOpen, setNavOpen] = useState(false);
    const [signingOut, setSigningOut] = useState(false);
    const { user, role } = useAuth();

    const visibleNavRoutes = useMemo(() => [...APP_NAV_ROUTES], []);

    const selectedMenuKey = useMemo(
        () => [resolveNavSelection(location.pathname, visibleNavRoutes)],
        [location.pathname, visibleNavRoutes]
    );

    const mobileTitle = useMemo(() => {
        const key = resolveNavSelection(location.pathname, visibleNavRoutes);
        const item = visibleNavRoutes.find((r) => r.path === key);
        return item?.label ?? 'VehiWheel Admin';
    }, [location.pathname, visibleNavRoutes]);

    const navItems: MenuProps['items'] = visibleNavRoutes.map(({ path, label }) => ({
        key: path,
        label: (
            <Link to={path} onClick={() => setNavOpen(false)}>
                {label}
            </Link>
        ),
    }));

    const handleSignOut = async () => {
        setSigningOut(true);
        try {
            await signOutApp();
        } finally {
            setSigningOut(false);
        }
    };

    const emailShort = user?.email?.split('@')[0] ?? user?.uid?.slice(0, 8);

    return (
        <Layout className="app-layout">
            <Header className="app-layout__header">
                <div className="demo-logo" />
                {isMobileNav ? (
                    <div className="app-layout__mobile-nav">
                        <span className="app-layout__mobile-title">{mobileTitle}</span>
                        {role ? (
                            <Tag color="blue" className="app-layout__role-tag app-layout__role-tag--mobile">
                                {role}
                            </Tag>
                        ) : null}
                        <div className="app-layout__header-actions">
                            <Button
                                type="text"
                                className="app-layout__sign-out"
                                icon={<LogoutOutlined />}
                                loading={signingOut}
                                onClick={handleSignOut}
                                aria-label="Sign out"
                            />
                            <Button
                                className="app-layout__mobile-menu-trigger"
                                type="text"
                                aria-label="Open navigation menu"
                                icon={<MenuOutlined />}
                                onClick={() => setNavOpen(true)}
                            />
                        </div>
                        <Drawer
                            title="Navigation"
                            placement="left"
                            width={280}
                            open={navOpen}
                            onClose={() => setNavOpen(false)}
                            styles={{ body: { padding: 0 } }}
                        >
                            <Menu
                                mode="inline"
                                selectedKeys={selectedMenuKey}
                                items={navItems}
                            />
                        </Drawer>
                    </div>
                ) : (
                    <>
                        <Menu
                            theme="dark"
                            mode="horizontal"
                            selectedKeys={selectedMenuKey}
                            items={navItems}
                            className="app-layout__menu"
                        />
                        <div className="app-layout__header-actions app-layout__header-actions--desktop">
                            {emailShort ? (
                                <Link
                                    to="/account"
                                    className="app-layout__user-hint app-layout__user-hint--link"
                                    title={user?.email ?? 'Account'}
                                >
                                    {emailShort}
                                </Link>
                            ) : null}
                            {role ? (
                                <Tag color="blue" className="app-layout__role-tag">
                                    {role}
                                </Tag>
                            ) : null}
                            <Button
                                type="text"
                                className="app-layout__sign-out"
                                icon={<LogoutOutlined />}
                                loading={signingOut}
                                onClick={handleSignOut}
                            >
                                Sign out
                            </Button>
                        </div>
                    </>
                )}
            </Header>

            <Content className="app-layout__content">
                <div className="vw-shell vw-shell--wide app-layout__content-shell">
                    <Outlet />
                </div>
            </Content>

            <Footer className="app-layout__footer">
                VehiWheelAdmin ©2026 Created by VehiWheel
            </Footer>
        </Layout>
    );
}
